/**
 * Cupo de expedientes activos del plan (issue #568, frontend).
 *
 * Una sola fuente para las tres pantallas que crean expedientes (Radar,
 * barra de acciones en lote, dialogo de idea) y para `/membresia`, que
 * lo muestra. Todas leen `GET /billing/my-plan`.
 *
 * **No agrega red.** Reusa la queryKey `["my-plan", daneCode]` y el
 * `staleTime` de `AssistantQuotaBadge`, que el `AppShell` monta en toda
 * pagina autenticada: cuando el usuario llega al Radar el plan ya esta
 * en cache.
 *
 * **Falla abierto, a proposito.** `isExhausted` es `true` unicamente con
 * dato cargado que diga que el cupo esta agotado. Si la consulta esta en
 * vuelo o fallo, devuelve `false` y el usuario puede intentar crear: el
 * backend tiene la ultima palabra (402 desde `TierGate`) y prefiere
 * dejar pasar un intento que termina en un mensaje diciente, antes que
 * bloquear a alguien que si tiene cupo. Mismo criterio con el que
 * `ExpedienteDetail` solo pinta el asistente con `planQuery.data`, para
 * no mostrar "Pro requerido" en falso.
 *
 * **`isUnlimited`.** El backend no tiene un "sin limite": a Pro le
 * asigna `_PRO_MAX_ACTIVE_PROJECTS = 9999` ("ilimitado en practica", ver
 * `backend/subsystems/billing/tier_gate.py`). Pintar ese centinela seria
 * "12 de 9999 usados". Ramificamos por tier y no por el numero porque el
 * 9999 es un detalle de implementacion del backend, mientras que
 * "expedientes ilimitados" es la promesa del plan Pro que ya aparece en
 * `RenovarMembresia` y en el propio mensaje de upsell del backend.
 */

import { useQuery } from "@tanstack/react-query";

import { fetchMyPlan, type QuotaUsageDTO, type TierName } from "@/lib/api";
import { useMunicipality } from "@/lib/municipality";

/** Mismo `staleTime` que `AssistantQuotaBadge` — comparten entrada de cache. */
const PLAN_STALE_TIME_MS = 5 * 60 * 1000;

/** queryKey compartida con el badge del sidebar y con `ExpedienteDetail`. */
export function myPlanQueryKey(daneCode: string | null): [string, string | null] {
  return ["my-plan", daneCode];
}

export interface ActiveProjectsQuota {
  /** Cuota cruda del plan, o `null` mientras no haya dato. */
  quota: QuotaUsageDTO | null;
  /** Tier del plan, o `null` mientras no haya dato. */
  tier: TierName | null;
  /** `true` SOLO con dato cargado y cupo agotado (ver docstring). */
  isExhausted: boolean;
  /** `true` cuando el plan no tiene tope practico (Pro). */
  isUnlimited: boolean;
  /** Si el plan incluye el asistente IA, o `null` mientras no haya dato (#629). */
  includesAiAssistant: boolean | null;
  /** `true` mientras la consulta del plan esta en vuelo. */
  isLoading: boolean;
}

export function useActiveProjectsQuota(): ActiveProjectsQuota {
  const { daneCode } = useMunicipality();
  const { data, isLoading } = useQuery({
    queryKey: myPlanQueryKey(daneCode),
    queryFn: () => fetchMyPlan(daneCode as string),
    enabled: Boolean(daneCode),
    staleTime: PLAN_STALE_TIME_MS,
    retry: 1,
  });

  if (!data) {
    return {
      quota: null,
      tier: null,
      isExhausted: false,
      isUnlimited: false,
      includesAiAssistant: null,
      isLoading,
    };
  }

  return {
    quota: data.active_projects,
    tier: data.tier,
    isExhausted: data.active_projects.is_exhausted,
    isUnlimited: data.tier === "pro",
    includesAiAssistant: data.includes_ai_assistant,
    isLoading: false,
  };
}
